/**
 * EmbedMapPage — `/embed/:token/:singular?/:itemId?`
 *
 * Embeddable shared map for iframes with the full map toolbar (layers panel,
 * 2D/3D, zooms — same as `/share`), a collapsible list toggle, the
 * URL-driven detail panel, and the brand badge. Reads URL params:
 *  - `list=hidden` — start with the list collapsed
 *  - `basemap=<id>` — initial basemap (must be allowed by the share)
 *  - `zoom=<n>` — informational; the map's start view comes from the config
 *
 * Shell-less; password gate respects the cross-site SameSite cookie constraint
 * (handled inside {@link PublicPasswordGate}).
 *
 * @module pages/EmbedMapPage
 */
import { useCallback, useState, type ReactElement } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { FloatingPanel } from '@/components/ui/FloatingPanel';
import { SlidePanel } from '@/components/ui/SlidePanel';
import { isValidBasemapId, type BasemapId } from '@/config/maplibre';
import {
  PublicArtifactDetail,
  PublicArtifactList,
  PublicListToggle,
  PublicMapShell,
  PublicShareGate,
  usePublicSharePage,
} from '@/components/share/public';

import styles from './EmbedMapPage.module.css';

export function EmbedMapPage(): ReactElement {
  const { token = '' } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const {
    status,
    passwordError,
    rateLimited,
    config,
    listData,
    mapData,
    descriptor,
    artifact,
    isDetailLoading,
    detailError,
    viewerFilters,
    setViewerFilters,
    handleSelect,
    handleSubmitPassword,
    closeOverlay,
  } = usePublicSharePage(token);

  const [listOpen, setListOpen] = useState(
    () => searchParams.get('list') !== 'hidden'
  );
  const toggleList = useCallback(() => setListOpen((open) => !open), []);
  const closeList = useCallback(() => setListOpen(false), []);

  if (status !== 'ready' || !config || !listData || !mapData) {
    return (
      <PublicShareGate
        status={status}
        passwordError={passwordError}
        rateLimited={rateLimited}
        onSubmitPassword={handleSubmitPassword}
      />
    );
  }

  const basemapParam = searchParams.get('basemap');
  const initialBasemapId: BasemapId | undefined =
    basemapParam && isValidBasemapId(basemapParam) ? basemapParam : undefined;

  return (
    <div className={styles.page} data-chromeless>
      <PublicMapShell
        token={token}
        config={config}
        initialBasemapId={initialBasemapId}
      />

      <FloatingPanel
        position="top-left"
        aria-label={t('share.public.listToggle')}
      >
        <PublicListToggle opened={listOpen} onToggle={toggleList} />
      </FloatingPanel>

      <SlidePanel opened={listOpen} onClose={closeList} withCloseButton>
        <PublicArtifactList
          config={config}
          listData={listData}
          viewerFilters={viewerFilters}
          onViewerFiltersChange={setViewerFilters}
          onSelect={handleSelect}
        />
      </SlidePanel>

      <PublicArtifactDetail
        descriptor={descriptor}
        artifact={artifact}
        isDetailLoading={isDetailLoading}
        detailError={detailError}
        config={config}
        listData={listData}
        token={token}
        onClose={closeOverlay}
      />
    </div>
  );
}
